/**
 * Sprint 23.7 — `<ThemeCard>`, the single shared language-theme card. Used in
 * BOTH the onboarding theme picker (`/welcome/theme`) and Settings → Appearance,
 * so the two never drift (visual-spec-checklist check 5). One `variant` prop
 * switches between the two shapes:
 *
 *   - "picker"   → compact: name + sample phrase, 2px info border + check when
 *                  selected. The smart-default highlight in onboarding.
 *   - "settings" → fuller: name + blurb + a sample action pill and status dot,
 *                  matching the Sprint 23 Appearance look exactly (no regression).
 */

import { type CSSProperties } from "react";
import {
  LANGUAGE_THEME_BLURBS,
  LANGUAGE_THEME_LABELS,
  LANGUAGE_THEME_NAMES,
  LANGUAGE_THEME_SAMPLE_PHRASE,
  type LanguageTheme,
} from "../../lib/themed-labels";

export interface ThemeCardProps {
  theme: LanguageTheme;
  selected: boolean;
  onPick: () => void;
  variant?: "picker" | "settings";
  disabled?: boolean;
}

export function ThemeCard({ theme, selected, onPick, variant = "picker", disabled = false }: ThemeCardProps) {
  if (variant === "settings") return <SettingsThemeCard theme={theme} selected={selected} onPick={onPick} disabled={disabled} />;
  return <PickerThemeCard theme={theme} selected={selected} onPick={onPick} disabled={disabled} />;
}

function PickerThemeCard({ theme, selected, onPick, disabled }: Omit<ThemeCardProps, "variant">) {
  const style: CSSProperties = {
    background: "var(--color-background-primary)",
    border: selected ? "2px solid var(--color-border-info)" : "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-md)",
    padding: selected ? "8.5px 10.5px" : "10px 12px", // compensate for the 1.5px border growth
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: disabled ? "wait" : "pointer",
    textAlign: "left",
    width: "100%",
    fontFamily: "var(--font-sans)",
  };

  return (
    <button type="button" role="radio" aria-checked={selected} disabled={disabled} onClick={onPick} style={style}>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {LANGUAGE_THEME_NAMES[theme]}
        </span>
        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
          {LANGUAGE_THEME_SAMPLE_PHRASE[theme]}
        </span>
      </span>
      {selected && (
        <i className="ti ti-check" style={{ fontSize: 14, color: "var(--color-text-info)", flexShrink: 0 }} aria-hidden="true" />
      )}
    </button>
  );
}

function SettingsThemeCard({ theme, selected, onPick, disabled }: Omit<ThemeCardProps, "variant">) {
  const labels = LANGUAGE_THEME_LABELS[theme];
  const style: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "12px 14px",
    borderRadius: "var(--border-radius-md)",
    border: selected ? "1px solid var(--color-text-primary)" : "var(--kuvio-border-default)",
    background: selected ? "var(--color-background-secondary)" : "var(--color-background-primary)",
    cursor: disabled ? "wait" : "pointer",
    textAlign: "left",
    fontFamily: "var(--font-sans)",
    width: "100%",
  };

  return (
    <button type="button" role="radio" aria-checked={selected} disabled={disabled} onClick={onPick} style={style}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {LANGUAGE_THEME_NAMES[theme]}
        </span>
        {selected && (
          <i className="ti ti-check" style={{ fontSize: 13, color: "var(--color-text-secondary)", marginLeft: "auto" }} aria-hidden="true" />
        )}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
        {LANGUAGE_THEME_BLURBS[theme]}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
        <span style={samplePillStyle}>
          <i className="ti ti-plus" style={{ fontSize: 10 }} aria-hidden="true" />
          {labels.addAction}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--color-text-tertiary)" }}>
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--kuvio-status-active)", display: "inline-block" }} />
          {labels.statusWorking}
        </span>
      </div>
    </button>
  );
}

const samplePillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  fontWeight: 500,
  padding: "2px 8px",
  borderRadius: "var(--kuvio-pill-radius)",
  background: "var(--color-background-tertiary)",
  color: "var(--color-text-secondary)",
};
